import { useState, useEffect } from 'react';
import { Navigate, Outlet, Link } from 'react-router-dom';
import { Loader2, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthContext } from '@/context/AuthContext';

// ── Loading spinner ───────────────────────────────────────────────────────────

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
    </div>
  );
}

// ── Force-password-change banner ──────────────────────────────────────────────

export function ForcePasswordBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuthContext();

  if (!user?.force_password_change || dismissed) return null;

  return (
    <div className="relative flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
      <span className="flex-1">
        Please change your temporary password.{' '}
        <Link
          to="/change-password"
          className="font-semibold underline underline-offset-2 hover:text-amber-700 transition-colors"
        >
          Change now →
        </Link>
      </span>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="p-0.5 rounded hover:bg-amber-100 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── ProtectedRoute ────────────────────────────────────────────────────────────

/**
 * Wraps all shell pages. Shows spinner while session loads, redirects to
 * /login if unauthenticated. The ForcePasswordBanner is rendered by AppShell
 * (between the fixed header and main content) so it is never occluded.
 */
export function ProtectedRoute() {
  const { isLoading, isAuthenticated } = useAuthContext();

  if (isLoading) return <FullScreenSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <Outlet />;
}

// ── ProtectedRouteOnly ────────────────────────────────────────────────────────

/**
 * Auth-check only — no AppShell, no banner. Used for standalone pages like
 * /change-password that need auth but render their own full-page layout.
 */
export function ProtectedRouteOnly({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuthContext();

  if (isLoading) return <FullScreenSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

// ── AdminRoute ────────────────────────────────────────────────────────────────

/**
 * Nested inside ProtectedRoute. Redirects non-admins to / with a toast.
 */
export function AdminRoute() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) toast.error('Access denied');
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}
